import type { SubmitScanRequest } from '@/api/types'
import {
  mindsetBloodOxygen,
  mindsetPulseRate,
  mindsetRespirationRate,
} from './mindset-result'
import type { MindsetFinalValues } from './mindset-types'
import { shenAiBloodPressure, shenAiRespirationRate } from './shenai-result'
import type { ShenAiMeasurementResults } from './shenai-types'

/**
 * What the two vendors of a hybrid scan handed back, before either has been read for anything.
 *
 * Both nullable, because either vendor can end a session having measured nothing at all - and the
 * composite below is the only place that combination is turned into an answer.
 */
export interface HybridVendorResults {
  readonly shenAi: ShenAiMeasurementResults | null | undefined
  readonly mindset: MindsetFinalValues | null | undefined
}

/**
 * Which vendor each number in a composite came from, for the console and for nothing else.
 *
 * It exists because a composite reading is the one submission in this app whose provenance cannot be
 * recovered from the submission itself: four integers arrive at
 * `VitalsAssessmentCompletedEvent` and nothing on the wire says that the pressure was measured by a
 * different SDK than the pulse. That is a property of `SubmitScanRequest`, not something this file
 * may fix (a vendor-per-vital field is a change to every consumer downstream), so the attribution
 * lives on the patient's own console beside the two raw results (docs/decisions.md D-37).
 */
export interface HybridAttribution {
  readonly sbp: 'shenai'
  readonly dbp: 'shenai'
  readonly hr: 'mindset'
  readonly rr: 'mindset' | 'shenai' | 'absent'
  readonly spo2: 'mindset' | 'absent'
}

export type HybridComposition =
  | {
      readonly composed: true
      readonly request: SubmitScanRequest
      readonly attribution: HybridAttribution
    }
  | {
      readonly composed: false
      /** What was missing, named for the log: a composite that cannot be built is worth explaining. */
      readonly missing: readonly ('bloodPressure' | 'pulseRate')[]
    }

/**
 * Composes the one reading a hybrid scan submits out of the two vendors that measured it.
 *
 * **The division is fixed and it is the whole point of the vendor** (docs/decisions.md D-37):
 *
 * - **Blood pressure is Shen.AI's, always.** It is the vital this flow exists to collect and the one
 *   the two vendors disagree about most: Shen answers in millimetres of mercury from a regression,
 *   while a Mindset clinic on the hypertension classifier answers with a class and no numbers. A
 *   composite that fell back to Mindset's pressure when Shen's was missing would be a scan that
 *   submits no pressure at all - which is the exact reading this arrangement exists to stop
 *   submitting. So there is NO fallback: no Shen pressure is no composite.
 * - **Pulse and blood oxygen are Mindset's, always.** Shen measures a heart rate too, and it is
 *   deliberately discarded here rather than averaged, preferred or filled in for a missing Mindset
 *   value. Two vendors' estimates of one vital are not two measurements to reconcile - reconciling them
 *   would be this app inventing a number neither SDK reported, on a medical page, and nothing here is
 *   qualified to do that. Shen measures no blood oxygen at all (shenai-types.ts), so that one has a
 *   single source whatever this rule said.
 * - **Respiration is Mindset's, and Shen.AI's when Mindset has none** - the one fallback, decided for
 *   KAN-3 (docs/decisions.md D-62). The concurrent runner ends Mindset when it settles its heart rate,
 *   which can come before its respiration; an iPhone 15 Pro scan then showed the patient no breathing
 *   rate while Shen.AI had measured one. It is a choice of source, never a reconciliation: one vendor's
 *   number, whole, and `attribution.rr` and the stored vendor readings say whose.
 *
 * **A missing optional stays missing.** `rr` and `spo2` are omitted rather than zeroed, exactly as
 * all three single-vendor mappers already do: a number in those fields is read as a measurement by
 * every consumer downstream of `VitalsAssessmentCompletedEvent`, and "the vendor could not measure
 * it" is not a number.
 */
export function composeHybridSubmitRequest(results: HybridVendorResults): HybridComposition {
  const bloodPressure = shenAiBloodPressure(results.shenAi)
  const mindset = results.mindset

  const hr = mindset ? mindsetPulseRate(mindset) : null
  const mindsetRr = mindset ? mindsetRespirationRate(mindset) : null
  const shenAiRr = mindsetRr === null ? shenAiRespirationRate(results.shenAi) : null
  const rr = mindsetRr ?? shenAiRr
  const spo2 = mindset ? mindsetBloodOxygen(mindset) : null

  const missing: ('bloodPressure' | 'pulseRate')[] = []
  if (!bloodPressure) missing.push('bloodPressure')
  if (hr === null) missing.push('pulseRate')

  if (!bloodPressure || hr === null) {
    return { composed: false, missing }
  }

  return {
    composed: true,
    request: {
      sbp: bloodPressure.sbp,
      dbp: bloodPressure.dbp,
      hr: Math.round(hr),
      ...(spo2 === null ? {} : { spo2: Math.round(spo2) }),
      ...(rr === null ? {} : { rr: Math.round(rr) }),
    },
    attribution: {
      sbp: 'shenai',
      dbp: 'shenai',
      hr: 'mindset',
      rr: mindsetRr !== null ? 'mindset' : shenAiRr !== null ? 'shenai' : 'absent',
      spo2: spo2 === null ? 'absent' : 'mindset',
    },
  }
}
